import type { OrgType } from './persona'

export type PropertyStatus = 'active' | 'inactive' | 'maintenance' | 'seasonal'

export type FlywayRegion = 'atlantic' | 'mississippi' | 'central' | 'pacific' | 'uk'

export type OwnershipType = 'owned' | 'leased'

export type HuntableSpecies =
  | 'deer'
  | 'waterfowl'
  | 'duck'
  | 'geese'
  | 'upland_bird'
  | 'coyote'
  | 'elk'
  | 'antelope'
  | 'hog'
  | 'turkey'
  | 'bear'
  | 'migratory_bird'
  | 'gator'
  | 'upland'
  | 'other'

export type BoundaryLineStyle = 'solid' | 'dashed' | 'dotted'

export type BoundaryLineWeight = 1 | 2 | 3

export type ZoneType = string

export interface KnownZoneType {
  value: string
  label: string
  group: string
}

export const KNOWN_ZONE_TYPES: readonly KnownZoneType[] = [
  { value: 'pond', label: 'Pond', group: 'Open Water' },
  { value: 'lake', label: 'Lake', group: 'Open Water' },
  { value: 'reservoir', label: 'Reservoir', group: 'Open Water' },
  { value: 'river', label: 'River', group: 'Open Water' },
  { value: 'creek', label: 'Creek/Stream', group: 'Open Water' },
  { value: 'slough', label: 'Slough', group: 'Open Water' },
  { value: 'bayou', label: 'Bayou', group: 'Open Water' },
  { value: 'canal', label: 'Canal/Ditch', group: 'Open Water' },
  { value: 'marsh', label: 'Marsh', group: 'Wetland Habitat' },
  { value: 'swamp', label: 'Swamp', group: 'Wetland Habitat' },
  { value: 'pothole', label: 'Pothole', group: 'Wetland Habitat' },
  { value: 'flooded_timber', label: 'Flooded Timber', group: 'Wetland Habitat' },
  { value: 'impoundment', label: 'Impoundment/Managed Wetland', group: 'Wetland Habitat' },
  { value: 'coastal', label: 'Coastal/Bay', group: 'Wetland Habitat' },
  { value: 'field', label: 'Agricultural Field', group: 'Fields & Ag' },
  { value: 'rice_field', label: 'Rice Field', group: 'Fields & Ag' },
  { value: 'grain_field', label: 'Grain Field', group: 'Fields & Ag' },
  { value: 'food_plot', label: 'Food Plot', group: 'Fields & Ag' },
  { value: 'pasture', label: 'Pasture/Hay Field', group: 'Fields & Ag' },
  { value: 'timber', label: 'Timber/Woods', group: 'Upland & Cover' },
  { value: 'bottomland', label: 'Bottomland', group: 'Upland & Cover' },
  { value: 'hardwood_ridge', label: 'Hardwood Ridge', group: 'Upland & Cover' },
  { value: 'grassland', label: 'Grassland/CRP', group: 'Upland & Cover' },
  { value: 'brush', label: 'Brush/Cover', group: 'Upland & Cover' },
  { value: 'other', label: 'Other', group: 'General' },
]

export function zoneTypeLabel(value: string): string {
  const known = KNOWN_ZONE_TYPES.find((type) => type.value === value)
  if (known) return known.label
  const cleaned = value.trim()
  if (cleaned.length === 0) return 'Other'
  return cleaned
    .split(/[_\s]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export type ZoneStatus = 'active' | 'inactive' | 'maintenance' | 'seasonal'

export type BlindType =
  | 'pit_blind'
  | 'layout_blind'
  | 'a_frame_blind'
  | 'boat_blind'
  | 'grass_blind'
  | 'brush_blind'
  | 'popup_blind'
  | 'floating_blind'
  | 'sinkbox_blind'
  | 'pontoon_blind'
  | 'elevated_box_blind'
  | 'concrete_blind'
  | 'timber_blind'
  | 'shoreline_blind'
  | 'portable_popup_blind'
  | 'fence_row_blind'
  | 'mudflat_blind'
  | 'natural_blind'
  | 'elevated_blind'
  | 'coffin_blind'
  | 'permanent_structure'
  | 'no_blind'
  | 'other'
  | 'pit'
  | 'layout'
  | 'a_frame'
  | 'pontoon'
  | 'boat'
  | 'grass'
  | 'brush'
  | 'popup'
  | 'floating'
  | 'sinkbox'

export type BlindStatus =
  | 'active'
  | 'inactive'
  | 'maintenance'
  | 'reserved'
  | 'out_of_service'

export type DirectionFacing =
  | 'N'
  | 'NE'
  | 'E'
  | 'SE'
  | 'S'
  | 'SW'
  | 'W'
  | 'NW'
  | 'N/S'
  | 'E/W'
  | 'NE/SE'
  | 'NW/SW'

export type WaterFeature =
  | '0_12'
  | '12_24'
  | '24_36'
  | '36_plus'
  | 'dry_field'

export type ManagedFoodSource =
  | 'millet'
  | 'corn'
  | 'soybeans'
  | 'milo'
  | 'wheat'
  | 'rice'
  | 'buckwheat'
  | 'rye'
  | 'oats'
  | 'wild_rice'
  | 'chufa'
  | 'other'

export type NaturalFoodSource =
  | 'millet_fields'
  | 'smartweed_wild_millet'
  | 'acorns_mast'
  | 'submerged_aquatic_vegetation'
  | 'mudflat_invertebrates'
  | 'other'

export type HuntingUnitSystem = 'wmu' | 'wmz' | 'gha' | 'mu'

export type HuntingUnit = {
  id: string
  country: string
  subdivision: string
  unitSystem: HuntingUnitSystem
  unitCode: string
  unitLabel: string
  unitLabelLong: string
  unitName?: string
  parentCode?: string
  parentName?: string
}

export type FolderSource = 'own' | 'inherited' | 'none'

export type MapFolder = {
  id: string
  orgId?: string
  orgType?: OrgType
  createdBy: string
  name: string
  displayOrder: number
  createdAt: string
  updatedAt: string
}

export type CreateMapFolderInput = {
  orgId: string
  orgType: OrgType
  name: string
  displayOrder?: number
}

export type UpdateMapFolderInput = {
  orgId: string
  orgType: OrgType
  name?: string
  displayOrder?: number
}

export type MapFolderDisplayOrderEntry = {
  folderId: string
  displayOrder: number
}

export const UNFILED_FOLDER_LABEL = 'Unfiled'

export function compareMapFolders(a: MapFolder, b: MapFolder): number {
  if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder
  return a.name.localeCompare(b.name)
}

export function resolveBucketFolderId(
  effectiveFolderId: string | undefined,
  knownFolderIds: ReadonlySet<string>,
): string | null {
  if (!effectiveFolderId) return null
  if (!knownFolderIds.has(effectiveFolderId)) return null
  return effectiveFolderId
}

export type GeoJsonPolygon = {
  type: 'Polygon'
  coordinates: number[][][]
}

export type Property = {
  id: string
  orgId?: string
  orgType?: OrgType
  folderId?: string
  folderDetached?: boolean
  effectiveFolderId?: string
  folderSource?: FolderSource
  name: string
  description?: string
  totalAcres?: number
  computedTotalAcres?: number
  totalPerimeterMeters?: number
  notes?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  addressLatitude?: number
  addressLongitude?: number
  clubhouseLatitude: number | null
  clubhouseLongitude: number | null
  flywayRegion: FlywayRegion | null
  status: PropertyStatus
  displayOrder?: number
  ownershipType?: OwnershipType
  leaseNotes?: string
  leaseAmount?: number
  leaseExpiration?: string
  landownerName?: string
  landownerPhone?: string
  landownerEmail?: string
  customBoundary?: Record<string, unknown>
  boundaryGeometry?: Record<string, unknown>
  attachedRobustIds?: string[]
  huntableSpecies?: HuntableSpecies[]
  boundaryLineColor?: string | null
  boundaryAreaColor?: string | null
  boundaryLineStyle?: 'solid' | 'dashed' | 'dotted' | null
  boundaryLineWeight?: 1 | 2 | 3 | null
  huntingUnitId?: string
  huntingUnit?: HuntingUnit
  createdAt: string
  updatedAt: string
}

export type LocationOccupancyMode = 'none' | 'capacity' | 'groups'

export type Zone = {
  id: string
  propertyId?: string
  orgId?: string
  orgType?: OrgType
  createdBy?: string
  folderId?: string
  folderDetached?: boolean
  effectiveFolderId?: string
  folderSource?: FolderSource
  name: string
  description?: string
  zoneType: ZoneType
  status: ZoneStatus
  latitude?: number
  longitude?: number
  acreage?: number
  maxHunters?: number
  occupancyMode?: LocationOccupancyMode
  maxGroups?: number
  requiresGuide: boolean
  seasonalAvailability?: string
  accessNotes?: string
  terrainNotes?: string
  equipmentNotes?: string
  typicalSpecies?: string[]
  isActive: boolean
  isPublic: boolean
  displayOrder: number
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  notes?: string
  createdAt: string
  updatedAt: string
  deletedAt?: string
  boundary?: GeoJsonPolygon
  colorIndex?: number
  boundaryLineColor?: string | null
  boundaryAreaColor?: string | null
  boundaryLineStyle?: 'solid' | 'dashed' | 'dotted' | null
  boundaryLineWeight?: 1 | 2 | 3 | null
  huntingUnitId?: string
  huntingUnit?: HuntingUnit
}

export type Blind = {
  id: string
  zoneId?: string
  propertyId?: string
  orgId?: string
  orgType?: OrgType
  createdBy?: string
  folderId?: string
  folderDetached?: boolean
  effectiveFolderId?: string
  folderSource?: FolderSource
  name: string
  description?: string
  blindType: BlindType
  status: BlindStatus
  capacity: number
  occupancyMode?: LocationOccupancyMode
  maxGroups?: number
  latitude?: number
  longitude?: number
  directionFacing?: DirectionFacing
  waterFeature?: WaterFeature
  managedFoodSource?: ManagedFoodSource[]
  naturalFoodSource?: NaturalFoodSource[]
  hasDogBox?: boolean
  isReservable?: boolean
  accessibility?: string
  notes?: string
  isActive: boolean
  color?: string | null
  huntingUnitId?: string
  huntingUnit?: HuntingUnit
  createdAt: string
  updatedAt: string
}

export type CreatePropertyParcel = {
  robustId: string
  latitude: number
  longitude: number
}

export type CreatePropertyInput = {
  orgId?: string
  orgType?: OrgType
  name: string
  description?: string
  totalAcres?: number
  notes?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  addressLatitude?: number
  addressLongitude?: number
  clubhouseLatitude: number
  clubhouseLongitude: number
  flywayRegion?: FlywayRegion
  status?: PropertyStatus
  displayOrder?: number
  ownershipType?: OwnershipType
  leaseNotes?: string
  leaseAmount?: number
  leaseExpiration?: string
  landownerName?: string
  landownerPhone?: string
  landownerEmail?: string
  huntableSpecies?: HuntableSpecies[]
  boundaryLineColor?: string
  boundaryAreaColor?: string | null
  boundaryLineStyle?: BoundaryLineStyle
  boundaryLineWeight?: BoundaryLineWeight
  customBoundary?: Record<string, unknown>
  autoLinkParcel?: boolean
  parcels?: CreatePropertyParcel[]
}

export type UpdatePropertyInput = {
  folderId?: string | null
  folderDetached?: boolean
  name?: string
  description?: string
  totalAcres?: number
  notes?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  addressLatitude?: number
  addressLongitude?: number
  clubhouseLatitude?: number | null
  clubhouseLongitude?: number | null
  flywayRegion?: FlywayRegion | null
  status?: PropertyStatus
  displayOrder?: number
  ownershipType?: OwnershipType
  leaseNotes?: string
  leaseAmount?: number
  leaseExpiration?: string
  landownerName?: string
  landownerPhone?: string
  landownerEmail?: string
  huntableSpecies?: HuntableSpecies[]
  boundaryLineColor?: string
  boundaryAreaColor?: string | null
  boundaryLineStyle?: BoundaryLineStyle
  boundaryLineWeight?: BoundaryLineWeight
  customBoundary?: Record<string, unknown> | null
}

export type CreateZoneInput = {
  propertyId?: string
  orgId?: string
  orgType?: OrgType
  folderId?: string
  name: string
  description?: string
  zoneType: ZoneType
  status?: ZoneStatus
  latitude?: number
  longitude?: number
  acreage?: number
  maxHunters?: number | null
  occupancyMode?: LocationOccupancyMode
  maxGroups?: number | null
  requiresGuide?: boolean
  seasonalAvailability?: string
  accessNotes?: string
  terrainNotes?: string
  equipmentNotes?: string
  typicalSpecies?: string[]
  isActive?: boolean
  isPublic?: boolean
  displayOrder?: number
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  notes?: string
  boundaryLineColor?: string | null
  boundaryAreaColor?: string | null
  boundaryLineStyle?: 'solid' | 'dashed' | 'dotted' | null
  boundaryLineWeight?: 1 | 2 | 3 | null
}

export type UpdateZoneInput = {
  folderId?: string | null
  folderDetached?: boolean
  name?: string
  description?: string
  zoneType?: ZoneType
  status?: ZoneStatus
  latitude?: number
  longitude?: number
  acreage?: number
  maxHunters?: number | null
  occupancyMode?: LocationOccupancyMode
  maxGroups?: number | null
  requiresGuide?: boolean
  seasonalAvailability?: string
  accessNotes?: string
  terrainNotes?: string
  equipmentNotes?: string
  typicalSpecies?: string[]
  isActive?: boolean
  isPublic?: boolean
  displayOrder?: number
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  notes?: string
  boundaryLineColor?: string | null
  boundaryAreaColor?: string | null
  boundaryLineStyle?: 'solid' | 'dashed' | 'dotted' | null
  boundaryLineWeight?: 1 | 2 | 3 | null
}

export type CreateBlindInput = {
  zoneId?: string
  propertyId?: string
  orgId?: string
  orgType?: OrgType
  folderId?: string
  name: string
  description?: string
  blindType: BlindType
  status?: BlindStatus
  capacity: number
  occupancyMode?: LocationOccupancyMode
  maxGroups?: number | null
  latitude?: number
  longitude?: number
  directionFacing?: DirectionFacing
  waterFeature?: WaterFeature
  managedFoodSource?: ManagedFoodSource[]
  naturalFoodSource?: NaturalFoodSource[]
  hasDogBox?: boolean
  isReservable?: boolean
  accessibility?: string
  notes?: string
  isActive?: boolean
  color?: string | null
}

export type UpdateBlindInput = {
  zoneId?: string | null
  propertyId?: string | null
  folderId?: string | null
  folderDetached?: boolean
  name?: string
  description?: string
  blindType?: BlindType
  status?: BlindStatus
  capacity?: number
  occupancyMode?: LocationOccupancyMode
  maxGroups?: number | null
  latitude?: number
  longitude?: number
  directionFacing?: DirectionFacing
  waterFeature?: WaterFeature
  managedFoodSource?: ManagedFoodSource[]
  naturalFoodSource?: NaturalFoodSource[]
  hasDogBox?: boolean
  isReservable?: boolean
  accessibility?: string
  notes?: string
  isActive?: boolean
  color?: string | null
}

export type CreatePropertyFeatureInput = {
  propertyId?: string
  orgId?: string
  orgType?: OrgType
  folderId?: string
  featureType: FeatureType
  name?: string
  notes?: string
  geometry: Record<string, unknown>
  zoneId?: string
  metadata?: Record<string, unknown>
  lineColor?: string | null
  lineStyle?: 'solid' | 'dashed' | 'dotted' | null
  lineWeight?: 1 | 2 | 3 | null
}

export type UpdatePropertyFeatureInput = {
  propertyId?: string | null
  folderId?: string | null
  folderDetached?: boolean
  name?: string
  notes?: string
  geometry?: Record<string, unknown>
  zoneId?: string | null
  metadata?: Record<string, unknown>
  lineColor?: string | null
  lineStyle?: 'solid' | 'dashed' | 'dotted' | null
  lineWeight?: 1 | 2 | 3 | null
}

export type BlindWithZoneName = Blind & {
  zoneName?: string
}

export type ZoneWithBlindCount = Zone & {
  blindCount?: number
}

export type FeatureType = 'parcel' | 'path' | 'scouting'

export type PropertyFeature = {
  id: string
  propertyId?: string
  orgId?: string
  orgType?: OrgType
  createdBy?: string
  folderId?: string
  folderDetached?: boolean
  effectiveFolderId?: string
  folderSource?: FolderSource
  featureType: FeatureType
  name?: string
  notes?: string
  geometry: Record<string, unknown>
  parcelExternalId?: string
  parcelExternalUpdatedAt?: string
  lastRefreshedAt?: string
  lastRequestedAt?: string
  acreage?: number
  ownerName?: string
  parcelNumber?: string
  parcelAddress?: string
  parcelMailingAddress?: string
  parcelLandUseClass?: string
  parcelCountyName?: string
  parcelStateAbbr?: string
  recordedBy?: string
  pointCount?: number
  zoneId?: string
  customLocationId?: string
  metadata?: Record<string, unknown>
  lineColor?: string | null
  lineStyle?: 'solid' | 'dashed' | 'dotted' | null
  lineWeight?: 1 | 2 | 3 | null
  createdAt: string
  updatedAt: string
}

export type ParcelData = {
  ownerName?: string
  parcelNumber?: string
  acreage?: number
  lastVerifiedAt?: string
}

export type CustomLocation = {
  id: string
  propertyId: string
  orgId?: string
  orgType?: OrgType
  createdBy: string
  folderId?: string
  folderDetached?: boolean
  effectiveFolderId?: string
  folderSource?: FolderSource
  name: string
  description?: string
  latitude: number
  longitude: number
  address?: string
  city?: string
  state?: string
  country?: string
  capacity?: number
  occupancyMode?: LocationOccupancyMode
  maxGroups?: number
  isActive: boolean
  deletedAt?: string
  createdAt: string
  updatedAt: string
  waypointType?: string | null
  color?: string | null
  notes?: string | null
  zoneId?: string | null
  category?: string
  huntingUnitId?: string
  huntingUnit?: HuntingUnit
}
